import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  MenuItem, Switch, TextField, Typography,
} from '@mui/material';

import {
  benutzerAnlegen, benutzerListe, benutzerLoeschen, benutzerPasswortSetzen, eigenesPasswortAendern,
  einstellungenLesen, einstellungenSpeichern, ich, testmail,
} from '../api';
import { Bestaetigung, Seitenkopf } from '../komponenten/Bausteine';
import { datum, fehlertext } from '../utils/format';

function Abschnitt({ titel, text, children }) {
  return (
    <Card>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontSize: '1.05rem' }}>{titel}</Typography>
          {text && <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '70ch' }}>{text}</Typography>}
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

const zweispaltig = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 };

export default function Einstellungen() {
  const [e, setE] = useState(null);
  const [smtpPasswort, setSmtpPasswort] = useState('');
  const [meldung, setMeldung] = useState(null);     // { art, text }
  const [selbst, setSelbst] = useState(null);
  const [benutzer, setBenutzer] = useState([]);
  const [pw, setPw] = useState({ alt: '', neu: '', neu2: '' });
  const [neuerBenutzer, setNeuerBenutzer] = useState(null);
  const [pwFuer, setPwFuer] = useState(null);
  const [weg, setWeg] = useState(null);
  const [testAn, setTestAn] = useState('');

  const ok = (text) => setMeldung({ art: 'success', text });
  const schief = (err) => setMeldung({ art: 'error', text: fehlertext(err) });

  const laden = useCallback(() => {
    einstellungenLesen().then((r) => setE(r.data)).catch(schief);
    benutzerListe().then((r) => setBenutzer(r.data)).catch(() => {});
    ich().then((r) => { setSelbst(r.data); setTestAn((alt) => alt || r.data.email); }).catch(() => {});
  }, []);
  useEffect(laden, [laden]);

  if (!e) return meldung ? <Alert severity={meldung.art}>{meldung.text}</Alert> : null;
  const setze = (teil) => setE((alt) => ({ ...alt, ...teil }));
  const an = (wert) => ['ja', 'true', '1', 'an'].includes(String(wert).toLowerCase());

  const speichern = async (felder, text) => {
    try {
      const daten = Object.fromEntries(felder.map((k) => [k, e[k]]));
      if (felder.includes('smtp_host') && smtpPasswort) daten.smtp_passwort = smtpPasswort;
      const r = await einstellungenSpeichern(daten);
      setE(r.data); setSmtpPasswort(''); ok(text);
    } catch (err) { schief(err); }
  };

  const passwortAendern = async () => {
    if (pw.neu !== pw.neu2) { setMeldung({ art: 'error', text: 'Die beiden neuen Passwörter stimmen nicht überein.' }); return; }
    try { await eigenesPasswortAendern(pw.alt, pw.neu); setPw({ alt: '', neu: '', neu2: '' }); ok('Passwort geändert.'); } catch (err) { schief(err); }
  };

  const mailFelder = ['smtp_host', 'smtp_port', 'smtp_sicherheit', 'smtp_benutzer', 'smtp_absender'];

  return (
    <Box>
      <Seitenkopf titel="Einstellungen" />
      {meldung && <Alert severity={meldung.art} sx={{ mb: 2 }} onClose={() => setMeldung(null)}>{meldung.text}</Alert>}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 860 }}>
        <Abschnitt titel="Mein Passwort" text={selbst ? `Angemeldet als ${selbst.email}. Mindestens 10 Zeichen.` : ''}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
            <TextField id="pw-alt" label="Bisheriges Passwort" type="password" autoComplete="current-password" value={pw.alt} onChange={(x) => setPw({ ...pw, alt: x.target.value })} />
            <TextField id="pw-neu" label="Neues Passwort" type="password" autoComplete="new-password" value={pw.neu} onChange={(x) => setPw({ ...pw, neu: x.target.value })} />
            <TextField id="pw-neu2" label="Neues Passwort wiederholen" type="password" autoComplete="new-password" value={pw.neu2} onChange={(x) => setPw({ ...pw, neu2: x.target.value })} />
          </Box>
          <Box><Button variant="contained" onClick={passwortAendern} disabled={!pw.alt || !pw.neu || !pw.neu2}>Passwort ändern</Button></Box>
        </Abschnitt>

        <Abschnitt titel="Portal-Benutzer" text="Jeder Zugang darf alles. Im Protokoll steht, wer was geändert hat. Nach 5 falschen Passwörtern ist ein Zugang 15 Minuten gesperrt.">
          {benutzer.map((b) => (
            <Box key={b.id} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600 }}>{b.name || b.email}{selbst?.id === b.id && ' (ich)'}</Typography>
                <Typography variant="body2" color="text.secondary">{b.email}{b.erstellt_am ? ` · seit ${datum(b.erstellt_am)}` : ''}</Typography>
              </Box>
              {selbst?.id !== b.id && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => setPwFuer({ ...b, neu: '' })}>Passwort setzen</Button>
                  <Button size="small" color="error" onClick={() => setWeg(b)}>Löschen</Button>
                </Box>
              )}
            </Box>
          ))}
          <Box><Button variant="outlined" onClick={() => setNeuerBenutzer({ email: '', name: '', passwort: '' })}>Benutzer anlegen</Button></Box>
        </Abschnitt>

        <Abschnitt titel="Mailversand" text="Für Erinnerungen vor dem Ablauf und für Verlängerungsanfragen. Das Portal steht in der DMZ: der Mailserver muss dort in der Firewall freigegeben sein.">
          <Box sx={zweispaltig}>
            <TextField id="smtp-host" label="Mailserver (SMTP)" value={e.smtp_host ?? ''} onChange={(x) => setze({ smtp_host: x.target.value })} placeholder="smtp.example.de" />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 2 }}>
              <TextField id="smtp-port" label="Port" value={e.smtp_port ?? ''} onChange={(x) => setze({ smtp_port: x.target.value })} />
              <TextField id="smtp-sicherheit" select label="Verschlüsselung" value={e.smtp_sicherheit || 'starttls'} onChange={(x) => setze({ smtp_sicherheit: x.target.value })}>
                <MenuItem value="starttls">STARTTLS (587)</MenuItem>
                <MenuItem value="ssl">SSL/TLS (465)</MenuItem>
                <MenuItem value="keine">Keine (25)</MenuItem>
              </TextField>
            </Box>
            <TextField id="smtp-benutzer" label="Benutzername" value={e.smtp_benutzer ?? ''} onChange={(x) => setze({ smtp_benutzer: x.target.value })} autoComplete="off" />
            <TextField
              id="smtp-passwort" label="Passwort" type="password" value={smtpPasswort} onChange={(x) => setSmtpPasswort(x.target.value)} autoComplete="new-password"
              helperText={e.smtp_passwort_gesetzt ? 'Ist hinterlegt. Nur ausfüllen, um es zu ändern.' : 'Noch keines hinterlegt.'}
            />
            <TextField id="smtp-absender" label="Absender-Adresse" value={e.smtp_absender ?? ''} onChange={(x) => setze({ smtp_absender: x.target.value })} placeholder="lizenz@example.de" />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button variant="contained" onClick={() => speichern(mailFelder, 'Mailversand gespeichert.')}>Mailversand speichern</Button>
            <TextField id="testmail-an" label="Testmail an" value={testAn} onChange={(x) => setTestAn(x.target.value)} sx={{ minWidth: 220 }} />
            <Button
              variant="outlined" disabled={!testAn}
              onClick={async () => { try { await testmail(testAn); ok(`Testmail an ${testAn} verschickt.`); } catch (err) { schief(err); } }}
            >
              Testmail senden
            </Button>
          </Box>
        </Abschnitt>

        <Abschnitt titel="Erinnerung vor Ablauf" text="30 und 7 Tage vor dem Ablauf einer Lizenz geht eine Mail raus – je Lizenz und Ablaufdatum nur einmal. An den Kunden selbst nur, wenn es in seiner Akte eingeschaltet ist.">
          <FormControlLabel
            control={<Switch checked={an(e.erinnerung_aktiv)} onChange={(x) => setze({ erinnerung_aktiv: x.target.checked ? 'ja' : 'nein' })} />}
            label="Erinnerungen verschicken"
          />
          <TextField
            id="erinnerung-empfaenger" label="Interne Empfänger" value={e.erinnerung_empfaenger ?? ''} onChange={(x) => setze({ erinnerung_empfaenger: x.target.value })}
            helperText="Mehrere Adressen mit Komma trennen. Hierhin gehen auch Verlängerungsanfragen."
          />
          <Box><Button variant="contained" onClick={() => speichern(['erinnerung_aktiv', 'erinnerung_empfaenger'], 'Erinnerungen gespeichert.')}>Erinnerungen speichern</Button></Box>
        </Abschnitt>

        <Abschnitt titel="Download-Schutz" text="Ohne Schutz kann jeder mit dem Link jede Version laden. Mit Schutz nur Installationen, die sich schon einmal gemeldet haben (auch Testinstallationen).">
          <Alert severity="warning">
            ProfiWare schickt seine Installations-ID erst ab Version 1.6.175 mit. Erst einschalten, wenn alle Kunden mindestens diese Version haben – ältere können sonst kein Update mehr laden.
          </Alert>
          <FormControlLabel
            control={<Switch checked={an(e.download_schutz)} onChange={(x) => setze({ download_schutz: x.target.checked ? 'ja' : 'nein' })} />}
            label="Installer nur für bekannte Installationen"
          />
          <Box><Button variant="contained" onClick={() => speichern(['download_schutz'], 'Download-Schutz gespeichert.')}>Download-Schutz speichern</Button></Box>
        </Abschnitt>

        <Abschnitt titel="Kontakt auf den Kundenseiten" text="Steht auf „Lizenz verlängern“ und in den Erinnerungsmails an Kunden.">
          <Box sx={zweispaltig}>
            <TextField id="kontakt-telefon" label="Telefon" value={e.kontakt_telefon ?? ''} onChange={(x) => setze({ kontakt_telefon: x.target.value })} />
            <TextField id="kontakt-email" label="E-Mail" value={e.kontakt_email ?? ''} onChange={(x) => setze({ kontakt_email: x.target.value })} />
          </Box>
          <Box><Button variant="contained" onClick={() => speichern(['kontakt_telefon', 'kontakt_email'], 'Kontakt gespeichert.')}>Kontakt speichern</Button></Box>
        </Abschnitt>
      </Box>

      <Dialog open={!!neuerBenutzer} onClose={() => setNeuerBenutzer(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Benutzer anlegen</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField id="benutzer-name" label="Name" value={neuerBenutzer?.name ?? ''} onChange={(x) => setNeuerBenutzer({ ...neuerBenutzer, name: x.target.value })} helperText="Erscheint im Protokoll" autoFocus />
          <TextField id="benutzer-email" label="E-Mail" type="email" value={neuerBenutzer?.email ?? ''} onChange={(x) => setNeuerBenutzer({ ...neuerBenutzer, email: x.target.value })} />
          <TextField id="benutzer-passwort" label="Passwort" type="password" autoComplete="new-password" value={neuerBenutzer?.passwort ?? ''} onChange={(x) => setNeuerBenutzer({ ...neuerBenutzer, passwort: x.target.value })} helperText="Mindestens 10 Zeichen" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNeuerBenutzer(null)}>Abbrechen</Button>
          <Button
            variant="contained" disabled={!neuerBenutzer?.email || !neuerBenutzer?.passwort}
            onClick={async () => { try { await benutzerAnlegen(neuerBenutzer); setNeuerBenutzer(null); ok('Benutzer angelegt.'); laden(); } catch (err) { setNeuerBenutzer(null); schief(err); } }}
          >
            Benutzer anlegen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!pwFuer} onClose={() => setPwFuer(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Passwort für {pwFuer?.email} setzen</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <TextField id="benutzer-neues-passwort" fullWidth label="Neues Passwort" type="password" autoComplete="new-password" autoFocus value={pwFuer?.neu ?? ''} onChange={(x) => setPwFuer({ ...pwFuer, neu: x.target.value })} helperText="Mindestens 10 Zeichen. Hebt auch eine Anmeldesperre auf." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwFuer(null)}>Abbrechen</Button>
          <Button
            variant="contained" disabled={!pwFuer?.neu}
            onClick={async () => { try { await benutzerPasswortSetzen(pwFuer.id, pwFuer.neu); ok(`Passwort für ${pwFuer.email} gesetzt.`); } catch (err) { schief(err); } setPwFuer(null); }}
          >
            Passwort setzen
          </Button>
        </DialogActions>
      </Dialog>

      <Bestaetigung
        offen={!!weg} titel={`Zugang für ${weg?.email} löschen?`} knopf="Zugang löschen"
        text="Die Person kann sich danach nicht mehr am Portal anmelden. Ihre Einträge im Protokoll bleiben."
        onNein={() => setWeg(null)} onJa={async () => { await benutzerLoeschen(weg.id); setWeg(null); laden(); }}
      />
    </Box>
  );
}
